import React, { useState } from "react";
import { Guild, Member } from "../services/api";

interface MembersProps {
  guilds: Guild[];
  selectedGuild: string;
  setSelectedGuild: (guildId: string) => void;
  members: Member[];
  loading: boolean;
}

export const Members: React.FC<MembersProps> = ({
  guilds,
  selectedGuild,
  setSelectedGuild,
  members,
  loading,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  // Unique roles from all members
  const allRolesMap = new Map<string, { id: string; name: string; color: string }>();
  members.forEach((m) => {
    m.roles?.forEach((r) => {
      if (!allRolesMap.has(r.id)) {
        allRolesMap.set(r.id, r);
      }
    });
  });
  const uniqueRoles = Array.from(allRolesMap.values());

  const filteredMembers = members.filter((m) => {
    const matchesSearch =
      m.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (m.displayName && m.displayName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRole =
      filterRole === "all" || m.roles?.some((r) => r.id === filterRole);
    return matchesSearch && matchesRole;
  });

  return (
    <div className="tab-content">
      <div className="card-header-row">
        <div>
          <h2>Gestione Membri & Ruoli</h2>
          <p className="subtitle">Visualizza gli utenti del server, i loro ruoli e lo stato di attività</p>
        </div>

        <div className="guild-picker">
          <label>Server:</label>
          <select
            value={selectedGuild}
            onChange={(e) => setSelectedGuild(e.target.value)}
            className="select-input"
          >
            {guilds.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="filter-bar">
        <input
          type="text"
          placeholder="🔍 Cerca utente per username o nickname..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />

        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="select-input role-filter"
        >
          <option value="all">Tutti i Ruoli ({members.length})</option>
          {uniqueRoles.map((r) => (
            <option key={r.id} value={r.id}>
              @{r.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading">Caricamento membri in corso...</div>
      ) : filteredMembers.length === 0 ? (
        <div className="empty-state">
          <p>Nessun membro corrisponde ai criteri di ricerca.</p>
        </div>
      ) : (
        <div className="members-grid">
          {filteredMembers.map((member) => (
            <div key={member.id} className="member-card">
              <div className="member-avatar-wrap">
                <img
                  src={member.avatar || "/default-avatar.png"}
                  alt={member.username}
                  className="member-avatar"
                  onError={(e: any) => {
                    e.target.src = "https://cdn.discordapp.com/embed/avatars/0.png";
                  }}
                />
                <span className={`presence-dot ${member.status || "offline"}`}></span>
              </div>

              <div className="member-info">
                <div className="member-title-row">
                  <h4>{member.displayName || member.username}</h4>
                  {member.isBot && <span className="bot-tag">BOT</span>}
                </div>
                <p className="member-handle">@{member.username}</p>

                <div className="member-roles">
                  {member.roles && member.roles.length > 0 ? (
                    member.roles.map((role) => (
                      <span
                        key={role.id}
                        className="role-badge"
                        style={{
                          borderColor: role.color !== "#000000" && role.color !== "0" ? role.color : "#5865F2",
                          color: role.color !== "#000000" && role.color !== "0" ? role.color : "#fff",
                        }}
                      >
                        {role.name}
                      </span>
                    ))
                  ) : (
                    <span className="no-roles">Nessun ruolo</span>
                  )}
                </div>

                <div className="member-joined">
                  <small>
                    Membro dal: {member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : "N/A"}
                  </small>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
